import React from 'react';
import { Button } from '../../../components/ui/button';
import { Input } from '../../../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../../components/ui/select';
import { SizeCombobox } from '../../../components/ui/size-combobox';
import { Plus, Trash2, RefreshCw, Truck, Tag, Percent, Package, ShoppingCart } from 'lucide-react';

const MERV_RATINGS = [8, 11, 13];
const RECURRENCE_OPTIONS = [
  { value: '2', label: 'Every 2 months' },
  { value: '4', label: 'Every 4 months' },
  { value: '6', label: 'Every 6 months' },
  { value: '8', label: 'Every 8 months' },
];

export const FilterSelector = ({
  orderItems, groupedProducts, totalFilters, customerDiscountPct, customerMarkupPct,
  isSubscription, setIsSubscription, recurrence, setRecurrence,
  updateItem, addItem, removeItem,
}) => (
  <>
    {/* MERV Rating Guide */}
    <div className="bg-gradient-to-r from-teal-50 to-sky-50 border border-teal-200 rounded-xl p-4 mb-5" data-testid="merv-guide">
      <h3 className="font-bold text-teal-800 text-sm mb-2 flex items-center gap-2">
        <Package className="h-4 w-4" /> What does MERV mean for you?
      </h3>
      <p className="text-xs text-slate-600 mb-3">
        MERV (Minimum Efficiency Reporting Value) measures how well a filter captures airborne particles. Higher MERV = cleaner air for your family.
      </p>
      <div className="grid grid-cols-3 gap-2 text-xs">
        <div className="bg-white rounded-lg p-2.5 border border-slate-200">
          <div className="font-bold text-amber-700 mb-1">MERV 8</div>
          <div className="text-slate-500">Catches dust, pollen & lint. Basic protection.</div>
        </div>
        <div className="bg-white rounded-lg p-2.5 border border-sky-200">
          <div className="font-bold text-sky-700 mb-1">MERV 11</div>
          <div className="text-slate-500">Also catches mold spores & pet dander. Great for allergy sufferers.</div>
        </div>
        <div className="bg-white rounded-lg p-2.5 border border-teal-300 ring-2 ring-teal-200">
          <div className="font-bold text-teal-700 mb-1">MERV 13 *</div>
          <div className="text-slate-500">Captures bacteria & smoke. Hospital-level air quality at home.</div>
        </div>
      </div>
      <p className="text-[11px] text-teal-700 mt-2 font-medium">* We recommend MERV 13 for families with children, elderly, or anyone with respiratory concerns. Breathe easier — your health is worth it.</p>
    </div>

    <div className="flex flex-wrap gap-3 mb-5">
      <div className="flex items-center gap-2 bg-sky-50 border border-sky-200 rounded-lg px-3 py-2 text-sm text-sky-800">
        <Truck className="h-4 w-4 flex-shrink-0" />
        <span className="font-medium">Free shipping on orders over $100</span>
      </div>
      <div className="flex items-center gap-2 bg-teal-50 border border-teal-200 rounded-lg px-3 py-2 text-sm text-teal-800">
        <Tag className="h-4 w-4 flex-shrink-0" />
        <span className="font-medium">Auto-delivery subscription available</span>
      </div>
      {customerDiscountPct > 0 && (
        <div className="flex items-center gap-2 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2 text-sm text-amber-800" data-testid="customer-discount-banner">
          <Percent className="h-4 w-4 flex-shrink-0" />
          <span className="font-medium">Your account discount: {customerDiscountPct}%</span>
        </div>
      )}
      {customerMarkupPct > 0 && (
        <div className="flex items-center gap-2 bg-orange-50 border border-orange-200 rounded-lg px-3 py-2 text-sm text-orange-800" data-testid="customer-markup-banner">
          <Percent className="h-4 w-4 flex-shrink-0" />
          <span className="font-medium">Custom pricing: +{customerMarkupPct}% applied</span>
        </div>
      )}
    </div>

    <div className="bg-white rounded-xl border shadow-sm mb-5" data-testid="filters-table">
      <div className="bg-slate-100 rounded-t-xl px-5 py-3 flex items-center justify-between">
        <h2 className="font-semibold text-slate-800">Filters Needed Today</h2>
        <span className="text-sm font-medium text-teal-700 bg-teal-50 px-3 py-1 rounded-full" data-testid="total-filters-badge">
          Total: {totalFilters} filter{totalFilters !== 1 ? 's' : ''}
        </span>
      </div>
      <div className="hidden md:grid border-b px-5 py-2.5 gap-4 text-xs font-bold text-slate-500 uppercase tracking-wider"
        style={{ gridTemplateColumns: '1.5fr 2.5fr 1.2fr 1fr 1.2fr 0.4fr' }}>
        <div>MERV</div><div>Filter Size</div>
        <div className="text-right">Price / Filter</div><div className="text-center">Qty</div>
        <div className="text-right">Line Total</div><div></div>
      </div>
      <div className="divide-y">
        {orderItems.map((item, index) => {
          const itemTotal = item.price && item.quantity ? (item.price * parseInt(item.quantity || 0)) : 0;
          return (
            <div key={index} data-testid={`filter-row-${index}`}>
              {/* Mobile */}
              <div className="md:hidden p-4 space-y-3">
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="text-xs font-semibold text-slate-500 mb-1 block">MERV</label>
                    <Select value={item.merv} onValueChange={(v) => updateItem(index, 'merv', v)}>
                      <SelectTrigger className="h-10" data-testid={`select-merv-${index}`}><SelectValue placeholder="MERV" /></SelectTrigger>
                      <SelectContent>{MERV_RATINGS.map(m => <SelectItem key={m} value={m.toString()}>MERV {m}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                  <div>
                    <label className="text-xs font-semibold text-slate-500 mb-1 block">Filter Size</label>
                    <SizeCombobox
                      value={item.size}
                      onValueChange={(v) => updateItem(index, 'size', v)}
                      groupedProducts={groupedProducts}
                      placeholder="Search size..."
                      data-testid={`select-size-${index}`}
                    />
                  </div>
                </div>
                <div className="grid grid-cols-3 gap-3 items-end">
                  <div>
                    <label className="text-xs font-semibold text-slate-500 mb-1 block">Price</label>
                    <div className="h-10 flex items-center text-sm font-semibold">{item.price > 0 ? `$${item.price.toFixed(2)}` : '-'}</div>
                  </div>
                  <div>
                    <label className="text-xs font-semibold text-slate-500 mb-1 block">Qty {item.minQty > 1 && <span className="text-amber-600">(min {item.minQty})</span>}</label>
                    <Input type="number" min={item.minQty} max={100} className="h-10 text-center" value={item.quantity}
                      onChange={(e) => updateItem(index, 'quantity', e.target.value)} data-testid={`input-qty-${index}`} />
                  </div>
                  <div className="flex items-end justify-between">
                    <div>
                      <label className="text-xs font-semibold text-slate-500 mb-1 block">Total</label>
                      <div className="h-10 flex items-center text-sm font-bold text-sky-700">{itemTotal > 0 ? `$${itemTotal.toFixed(2)}` : '-'}</div>
                    </div>
                    {orderItems.length > 1 && (
                      <button type="button" onClick={() => removeItem(index)} className="h-10 px-2 text-red-400 hover:text-red-600"><Trash2 className="w-4 h-4" /></button>
                    )}
                  </div>
                </div>
              </div>
              {/* Desktop */}
              <div className="hidden md:grid px-5 py-3 gap-4 items-center"
                style={{ gridTemplateColumns: '1.5fr 2.5fr 1.2fr 1fr 1.2fr 0.4fr' }}>
                <Select value={item.merv} onValueChange={(v) => updateItem(index, 'merv', v)}>
                  <SelectTrigger className="h-10" data-testid={`select-merv-desktop-${index}`}><SelectValue placeholder="MERV" /></SelectTrigger>
                  <SelectContent>{MERV_RATINGS.map(m => <SelectItem key={m} value={m.toString()}>MERV {m}</SelectItem>)}</SelectContent>
                </Select>
                <SizeCombobox
                  value={item.size}
                  onValueChange={(v) => updateItem(index, 'size', v)}
                  groupedProducts={groupedProducts}
                  placeholder="Search size..."
                  data-testid={`select-size-desktop-${index}`}
                />
                <div className="text-right text-sm font-semibold text-slate-700">
                  {item.price > 0 ? `$${item.price.toFixed(2)}` : <span className="text-slate-400">-</span>}
                </div>
                <div className="flex flex-col items-center">
                  <Input type="number" min={item.minQty} max={100} className="h-10 text-center" value={item.quantity}
                    onChange={(e) => updateItem(index, 'quantity', e.target.value)} data-testid={`input-qty-desktop-${index}`} />
                  {item.minQty > 1 && <span className="text-[10px] text-amber-600 mt-0.5" data-testid={`moq-hint-${index}`}>Min: {item.minQty}</span>}
                </div>
                <div className="text-right text-sm font-bold text-sky-700">
                  {itemTotal > 0 ? `$${itemTotal.toFixed(2)}` : <span className="text-slate-400">-</span>}
                </div>
                <div className="flex justify-center">
                  {orderItems.length > 1 && (
                    <button type="button" onClick={() => removeItem(index)} className="text-red-400 hover:text-red-600 p-1"><Trash2 className="w-4 h-4" /></button>
                  )}
                </div>
              </div>
            </div>
          );
        })}
      </div>
      <div className="p-4 border-t">
        <Button type="button" variant="outline" onClick={addItem}
          className="w-full border-dashed border-2 text-slate-500 hover:text-sky-600 hover:border-sky-400" data-testid="add-filter-btn">
          <Plus className="h-4 w-4 mr-2" /> Add Another Filter Size
        </Button>
      </div>
    </div>

    <div className="rounded-xl border mb-5 bg-white" data-testid="purchase-type-section">
      <div className="bg-slate-100 rounded-t-xl px-5 py-3">
        <h2 className="font-semibold text-slate-800">How would you like to order?</h2>
      </div>
      <div className="p-4 grid grid-cols-1 sm:grid-cols-2 gap-3">
        {/* One-Time Purchase */}
        <button
          type="button"
          onClick={() => setIsSubscription(false)}
          className={`relative flex flex-col items-center text-center p-5 rounded-xl border-2 transition-all ${
            !isSubscription
              ? 'border-sky-500 bg-sky-50 ring-2 ring-sky-200'
              : 'border-gray-200 bg-white hover:border-gray-300'
          }`}
          data-testid="purchase-type-onetime"
        >
          <div className={`w-12 h-12 rounded-full flex items-center justify-center mb-3 ${!isSubscription ? 'bg-sky-100' : 'bg-gray-100'}`}>
            <ShoppingCart className={`h-6 w-6 ${!isSubscription ? 'text-sky-600' : 'text-gray-400'}`} />
          </div>
          <p className={`text-sm font-bold ${!isSubscription ? 'text-sky-800' : 'text-gray-700'}`}>One-Time Purchase</p>
          <p className="text-xs text-gray-500 mt-1">Buy now, no commitment</p>
          {!isSubscription && (
            <div className="absolute top-2 right-2 w-5 h-5 bg-sky-500 rounded-full flex items-center justify-center">
              <svg className="w-3 h-3 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" /></svg>
            </div>
          )}
        </button>

        {/* Subscribe & Auto-Delivery */}
        <button
          type="button"
          onClick={() => setIsSubscription(true)}
          className={`relative flex flex-col items-center text-center p-5 rounded-xl border-2 transition-all ${
            isSubscription
              ? 'border-teal-500 bg-teal-50 ring-2 ring-teal-200'
              : 'border-gray-200 bg-white hover:border-gray-300'
          }`}
          data-testid="purchase-type-subscription"
        >
          <div className={`w-12 h-12 rounded-full flex items-center justify-center mb-3 ${isSubscription ? 'bg-teal-100' : 'bg-gray-100'}`}>
            <RefreshCw className={`h-6 w-6 ${isSubscription ? 'text-teal-600' : 'text-gray-400'}`} />
          </div>
          <p className={`text-sm font-bold ${isSubscription ? 'text-teal-800' : 'text-gray-700'}`}>Subscribe & Auto-Deliver</p>
          <p className="text-xs text-gray-500 mt-1">Hassle-free recurring delivery</p>
          {isSubscription && (
            <div className="absolute top-2 right-2 w-5 h-5 bg-teal-500 rounded-full flex items-center justify-center">
              <svg className="w-3 h-3 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" /></svg>
            </div>
          )}
        </button>
      </div>
      {isSubscription && (
        <div className="px-5 pb-4" data-testid="recurrence-selector">
          <div className="flex items-center gap-3 bg-teal-50 border border-teal-200 rounded-lg p-3">
            <label className="text-xs font-semibold text-teal-700 whitespace-nowrap">Delivery Every: <span className="text-red-500">*</span></label>
            <Select value={recurrence} onValueChange={setRecurrence}>
              <SelectTrigger className="w-52 h-9 bg-white" data-testid="select-recurrence"><SelectValue /></SelectTrigger>
              <SelectContent>{RECURRENCE_OPTIONS.map(opt => <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>)}</SelectContent>
            </Select>
            {customerDiscountPct > 0 && (
              <span className="text-xs text-amber-600 font-medium ml-auto">Your {customerDiscountPct}% discount applies!</span>
            )}
          </div>
        </div>
      )}
    </div>
  </>
);
